import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { CommandBar } from "@/components/CommandBar";
import { Search, Menu, X, Command, Bot, Settings, LogOut, UserRound, Sparkles, ChevronsUpDown, ShieldCheck, Briefcase, Building2, Sun, Moon, Monitor, Zap, Check, House, Compass, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useBranding } from "@/hooks/useBranding";
import { useTheme } from "@/hooks/useTheme";
import { EMBEDDED } from "@/lib/embedded";

const NAV = {
  hiring: [
    { to: "/recruiter", label: "Home", end: true },
    { to: "/recruiter/jobs", label: "Jobs", match: ["/recruiter/jobs", "/recruiter/qbank", "/recruiter/trial-report"] },
    { to: "/recruiter/pipeline", label: "Candidates", match: ["/recruiter/pipeline", "/recruiter/candidate/", "/recruiter/compare/"], badge: true },
    { to: "/recruiter/interviews", label: "Interviews" },
    { to: "/recruiter/analytics", label: "Insights" },
  ],
  candidate: [
    { to: "/candidate", label: "Home", end: true },
    { to: "/candidate/jobs", label: "Jobs", match: ["/candidate/jobs", "/candidate/apply", "/candidate/auto-apply"] },
    { to: "/candidate/applications", label: "Applications" },
    { to: "/candidate/profile", label: "Profile", match: ["/candidate/profile", "/candidate/onboarding"] },
  ],
  gov: [
    { to: "/admin", label: "Overview", end: true },
    { to: "/admin/users", label: "Users" },
    { to: "/admin/audit", label: "Audit log" },
    { to: "/admin/bias", label: "Fairness" },
    { to: "/admin/settings", label: "AI & data" },
  ],
};
const TAB_ICONS = { Home: House, Jobs: Compass, Applications: ClipboardList, Profile: UserRound };
const ROLE_SUB = { recruiter: "Recruiter", admin: "Administrator", candidate: "Candidate", super_admin: "Platform super-admin" };

let badgeCache = { at: 0, value: 0 };
function useReviewBadge(enabled) {
  const [n, setN] = useState(badgeCache.value);
  useEffect(() => {
    if (!enabled || Date.now() - badgeCache.at < 60000) return;
    api.get("/dashboard/recruiter").then(({ data }) => { badgeCache = { at: Date.now(), value: data.needs_review || 0 }; setN(badgeCache.value); }).catch(() => {});
  }, [enabled]);
  return n;
}

function initials(name) {
  return (name || "?").trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [cmdOpen, setCmdOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const brand = useBranding();
  const { mode: themeMode, cycle: cycleTheme } = useTheme();
  const role = user?.role;
  const path = loc.pathname;
  const owner = role === "recruiter" && user?.org_role === "owner";
  const govRole = ["admin", "super_admin"].includes(role);
  const gov = path.startsWith("/admin") || path.startsWith("/superadmin") || (govRole && !path.startsWith("/recruiter") && !path.startsWith("/candidate"));
  const mode = gov ? "gov" : role === "candidate" ? "candidate" : "hiring";
  const items = mode === "gov" && role === "super_admin" ? [{ to: "/superadmin", label: "Organizations", match: ["/superadmin"] }, ...NAV.gov] : NAV[mode];
  const canSwitch = owner || govRole;
  const badge = useReviewBadge(mode === "hiring");

  useEffect(() => {
    const h = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setCmdOpen(true); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);
  useEffect(() => { setNavOpen(false); }, [path]);

  const isActive = (item) => item.end ? path === item.to : (item.match || [item.to]).some((m) => path.startsWith(m));
  const signOut = () => { logout(); nav("/login"); };

  if (EMBEDDED) return <main className="min-w-0 bg-background">{children}</main>;

  const BrandMark = () => (
    <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="brand-logo">
      {brand.brand_logo_url
        ? <img src={brand.brand_logo_url} alt={brand.brand_name} className="h-[30px] w-[30px] rounded-[9px] object-contain bg-white/5" />
        : <div className="h-[30px] w-[30px] rounded-[9px] bg-gradient-to-br from-primary to-teal-600 flex items-center justify-center text-[#04130D]"><Sparkles className="h-4 w-4" /></div>}
      <span className="font-display font-semibold text-[17px] hidden sm:block">{brand.brand_name}</span>
    </Link>
  );

  const NavItem = ({ item, mobile }) => {
    const active = isActive(item);
    return (
      <button onClick={() => nav(item.to)} data-testid={`nav-${item.label.replace(/[^a-z]+/gi, "-").toLowerCase()}`} data-active={active} aria-current={active ? "page" : undefined}
        className={cn("flex items-center gap-2 rounded-lg text-sm font-medium transition-colors duration-150",
          mobile ? "w-full px-3 py-2.5" : "px-3 py-2",
          active ? "bg-foreground/[0.07] text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04]")}>
        {item.label}
        {item.badge && badge > 0 && <span className="text-[11px] leading-none px-1.5 py-0.5 rounded-full bg-amber-400/15 text-amber-600 dark:text-amber-300" data-testid="nav-candidates-badge">{badge}</span>}
      </button>
    );
  };

  const Switcher = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="workspace-switcher">
          {mode === "gov" ? <ShieldCheck className="h-3.5 w-3.5" /> : <Briefcase className="h-3.5 w-3.5" />}
          {mode === "gov" ? (path.startsWith("/superadmin") ? "Platform" : "Governance") : "Hiring"}
          <ChevronsUpDown className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">Switch workspace</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => nav("/recruiter")} data-testid="switch-hiring"><Briefcase className="h-4 w-4 mr-2" /> Hiring workspace {mode !== "gov" && <Check className="h-3.5 w-3.5 ml-auto" />}</DropdownMenuItem>
        <DropdownMenuItem onClick={() => nav("/admin")} data-testid="switch-governance"><ShieldCheck className="h-4 w-4 mr-2" /> Governance {mode === "gov" && !path.startsWith("/superadmin") && <Check className="h-3.5 w-3.5 ml-auto" />}</DropdownMenuItem>
        {role === "super_admin" && <DropdownMenuItem onClick={() => nav("/superadmin")} data-testid="switch-platform"><Building2 className="h-4 w-4 mr-2" /> Platform console {path.startsWith("/superadmin") && <Check className="h-3.5 w-3.5 ml-auto" />}</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const ThemeIcon = themeMode === "light" ? Sun : themeMode === "dark" ? Moon : Monitor;
  const sub = owner ? "Organization owner" : ROLE_SUB[role];

  const ProfileMenu = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2.5 rounded-lg pl-1 pr-1.5 lg:pr-2 py-1 hover:bg-foreground/[0.05] transition-colors" data-testid="profile-menu-trigger">
          <span className="h-8 w-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold">{initials(user?.name)}</span>
          <span className="hidden lg:block text-left leading-tight">
            <span className="block text-[13px] font-semibold max-w-[140px] truncate">{user?.name}</span>
            <span className="block text-[11px] text-muted-foreground max-w-[140px] truncate">{sub}</span>
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel>
          <div className="text-sm font-medium truncate">{user?.name}</div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {role === "candidate" && <DropdownMenuItem onClick={() => nav("/candidate/profile")} data-testid="menu-profile"><UserRound className="h-4 w-4 mr-2" /> My profile</DropdownMenuItem>}
        {role === "candidate" && <DropdownMenuItem onClick={() => nav("/candidate/auto-apply")} data-testid="menu-auto-apply"><Zap className="h-4 w-4 mr-2" /> Auto-apply agent</DropdownMenuItem>}
        {mode !== "gov" && <DropdownMenuItem onClick={() => nav("/copilot")} data-testid="menu-assistant"><Bot className="h-4 w-4 mr-2" /> Open Copilot</DropdownMenuItem>}
        {canSwitch && mode !== "gov" && <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-governance"><ShieldCheck className="h-4 w-4 mr-2" /> Governance</DropdownMenuItem>}
        {canSwitch && mode === "gov" && <DropdownMenuItem onClick={() => nav("/recruiter")} data-testid="menu-hiring"><Briefcase className="h-4 w-4 mr-2" /> Hiring workspace</DropdownMenuItem>}
        <DropdownMenuItem onClick={cycleTheme} data-testid="theme-toggle"><ThemeIcon className="h-4 w-4 mr-2" /> Theme: <span className="capitalize ml-1">{themeMode}</span></DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => nav("/account")} data-testid="menu-account"><Settings className="h-4 w-4 mr-2" /> Account & security</DropdownMenuItem>
        <DropdownMenuItem onClick={signOut} data-testid="logout-btn" className="text-rose-500 focus:text-rose-500"><LogOut className="h-4 w-4 mr-2" /> Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="h-[60px] border-b border-border/70 bg-background/85 backdrop-blur-xl sticky top-0 z-40" data-testid="app-header">
        <div className="app-container h-full flex items-center gap-3 md:gap-5">
          <Button variant="ghost" size="icon" className={cn("md:hidden -ml-2", mode === "candidate" && "hidden")} onClick={() => setNavOpen(true)} data-testid="open-nav" aria-label="Open navigation"><Menu className="h-5 w-5" /></Button>
          <BrandMark />
          {canSwitch && <Switcher />}
          <nav className="hidden md:flex items-center gap-1" aria-label="Primary" data-testid="primary-nav">
            {items.map((item) => <NavItem key={item.to} item={item} />)}
          </nav>
          <div className="flex-1" />
          <button onClick={() => setCmdOpen(true)} data-testid="command-bar-trigger" aria-label="Search or ask Copilot"
            className="flex items-center gap-2.5 h-9 px-3 rounded-[9px] border border-border text-muted-foreground text-[13px] hover:border-primary/40 hover:text-foreground transition-colors sm:w-60 lg:w-64">
            <Search className="h-4 w-4 shrink-0" />
            <span className="hidden sm:block flex-1 min-w-0 text-left truncate">{mode === "candidate" ? "Search or ask Career Copilot" : "Search or ask Copilot"}</span>
            <kbd className="hidden sm:flex items-center gap-0.5 font-mono text-[11px] px-1.5 py-0.5 rounded border border-border"><Command className="h-2.5 w-2.5" />K</kbd>
          </button>
          <ProfileMenu />
        </div>
      </header>

      {navOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="nav-drawer">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setNavOpen(false)} data-testid="nav-backdrop" />
          <aside className="absolute left-0 top-0 h-full w-72 bg-card border-r border-border p-4 flex flex-col gap-1 animate-in slide-in-from-left duration-200">
            <div className="flex items-center justify-between mb-4"><BrandMark /><Button variant="ghost" size="icon" onClick={() => setNavOpen(false)} data-testid="close-nav" aria-label="Close navigation"><X className="h-5 w-5" /></Button></div>
            {items.map((item) => <NavItem key={item.to} item={item} mobile />)}
            {mode !== "gov" && <button onClick={() => nav("/copilot")} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground" data-testid="drawer-copilot"><Bot className="h-4 w-4" /> Copilot</button>}
            {canSwitch && <button onClick={() => nav(mode === "gov" ? "/recruiter" : "/admin")} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground mt-auto border-t border-border pt-4" data-testid="drawer-switch">{mode === "gov" ? <><Briefcase className="h-4 w-4" /> Hiring workspace</> : <><ShieldCheck className="h-4 w-4" /> Governance</>}</button>}
          </aside>
        </div>
      )}

      <main className={cn("flex-1 min-w-0", mode === "candidate" && "pb-16 md:pb-0")}>{children}</main>
      {mode === "candidate" && (
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 h-16 grid grid-cols-4 border-t border-border/70 bg-background/95 backdrop-blur-xl" aria-label="Primary" data-testid="candidate-bottom-nav">
          {items.map((item) => {
            const Icon = TAB_ICONS[item.label];
            const active = isActive(item);
            return (
              <button key={item.to} onClick={() => nav(item.to)} aria-current={active ? "page" : undefined} data-testid={`bottom-nav-${item.label.toLowerCase()}`}
                className={cn("flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors", active ? "text-primary" : "text-muted-foreground hover:text-foreground")}>
                <Icon className="h-5 w-5" />{item.label}
              </button>
            );
          })}
        </nav>
      )}
      <CommandBar open={cmdOpen} setOpen={setCmdOpen} />
    </div>
  );
}
